import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ShiftRegisterPage } from '@/api/models';
import { ShiftQueryKeys, type ShiftRegisterParams } from './shiftQueryKeys';

/**
 * The largest page bos-api serves. One day of one branch's shifts fits in it
 * many times over, so the register reads a day as a single page.
 */
export const SHIFT_REGISTER_PAGE_SIZE = 200;

/**
 * One day of the branch's shift register (`GET /shifts`), optionally narrowed
 * to the seller who opened the shift. The server scopes it to the caller's
 * branch. Unwraps the `ApiResponse` envelope and throws its `ErrorResponse`, so
 * TanStack Query sees a failed read as a failure.
 */
export const useShiftRegisterQuery = (params: ShiftRegisterParams) => {
    const { apiClient } = useApi();

    return useQuery<ShiftRegisterPage>({
        queryKey: ShiftQueryKeys.register(params),
        queryFn: async () => {
            const result = await apiClient.shifts.getRegister({
                from: params.date,
                to: params.date,
                sellerId: params.sellerId ?? undefined,
                page: 1,
                pageSize: SHIFT_REGISTER_PAGE_SIZE,
            });
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        // An open shift's figures change as it closes; keep the list fresh.
        staleTime: 0,
    });
};
